import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCrmLayout } from '../../../components/crm/CRMLayout';
import BulkImportModal from '../../../components/crm/BulkImportModal';

const OverviewTab: React.FC = () => {
  const { activeOrgId } = useCrmLayout();
  const [showImportModal, setShowImportModal] = useState(false);

  return (
    <div className="space-y-6">
      {/* Getting Started */}
      <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">Getting started</h2>
              <Link
                to="/help?article=crm-overview-dashboard"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
              >
                ❓ Overview help
              </Link>
            </div>
            <p className="text-sm text-gray-600">
              Build momentum by importing data, reviewing metrics, and aligning your team.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.location.hash = 'contacts'}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
            >
              View contacts
            </button>
            <button
              type="button"
              onClick={() => window.location.hash = 'deals'}
              className="inline-flex items-center gap-2 rounded-lg border border-primary/30 px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
            >
              View pipeline
            </button>
          </div>
        </div>
      </section>

      {/* Workspace Shortcuts */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">Quick Access</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              onClick={() => window.location.hash = 'contacts'}
              className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5"
            >
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">👥 Contacts</h4>
              <p className="mt-2 text-xs text-gray-500">Manage people, roles, and engagement.</p>
            </button>
            <button
              onClick={() => window.location.hash = 'companies'}
              className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5"
            >
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">🏢 Companies</h4>
              <p className="mt-2 text-xs text-gray-500">Track accounts and buying committees.</p>
            </button>
            <button
              onClick={() => window.location.hash = 'deals'}
              className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5"
            >
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">💰 Deals</h4>
              <p className="mt-2 text-xs text-gray-500">Track opportunities and revenue.</p>
            </button>
            <button
              onClick={() => window.location.hash = 'activities'}
              className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5"
            >
              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">📅 Activities</h4>
              <p className="mt-2 text-xs text-gray-500">View activity feed and calendar.</p>
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">Recommended Setup</h3>
          <ol className="space-y-3 text-sm text-gray-600">
            <li>
              <button
                onClick={() => setShowImportModal(true)}
                className="group w-full rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm"
              >
                <p className="font-semibold text-gray-900 group-hover:text-primary">1. Import historical data →</p>
                <p className="mt-1 text-xs text-gray-500">Bring in contacts and companies for full context.</p>
              </button>
            </li>
            <li>
              <Link
                to="/crm/deals-board"
                className="group block w-full rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm"
              >
                <p className="font-semibold text-gray-900 group-hover:text-primary">2. Customize pipeline stages →</p>
                <p className="mt-1 text-xs text-gray-500">Adjust deal stages to match your process.</p>
              </Link>
            </li>
            <li>
              <Link
                to="/crm/workflows"
                className="group block w-full rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-left transition hover:border-primary/30 hover:bg-primary/5 hover:shadow-sm"
              >
                <p className="font-semibold text-gray-900 group-hover:text-primary">3. Set up team workflows →</p>
                <p className="mt-1 text-xs text-gray-500">Automate follow-ups and notifications.</p>
              </Link>
            </li>
          </ol>
        </div>
      </section>

      {/* Other CRM Modules */}
      <section className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">Other CRM Modules</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Link
            to="/investor-crm"
            className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 transition hover:border-primary/30 hover:bg-primary/5"
          >
            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">💼 Investor CRM</h4>
            <p className="mt-2 text-xs text-gray-500">Track fundraising pipeline and investor relationships.</p>
          </Link>
          <Link
            to="/supplier-hub"
            className="group rounded-lg border border-gray-100 bg-gray-50/60 p-4 transition hover:border-primary/30 hover:bg-primary/5"
          >
            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary">🏭 Supplier Hub</h4>
            <p className="mt-2 text-xs text-gray-500">Manage supplier relationships and procurement.</p>
          </Link>
        </div>
      </section>

      <BulkImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        organizationId={activeOrgId || ''}
        onImportComplete={() => {
          // Optionally refresh data here
        }}
      />
    </div>
  );
};

export default OverviewTab;
